import {
  breakpointSizes,
  ButtonDesigns,
  getBreakpointDown,
  getFontCss,
  HoverableLink,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import Button, { ButtonTypes } from 'src/components/Button/Button'
import CloseButton, {
  CloseButtonDesigns,
} from 'src/components/CloseButton/CloseButton'
import RightFlyout from 'src/components/RightFlyout/RightFlyout'
import Trns from 'src/localization/Trns'
import { createSetFiltersFlyoutOpen } from 'src/redux/actions/app.actions'
import { selectCalculatedBackgroundLocation } from 'src/redux/selectors/router.selectors'
import { css } from 'styled-components'

interface FiltersFlyoutProps {
  className?: string
  onClose?: () => void
  onReset: () => void
  onSave?: ()=> void
  title: string | React.ReactElement
  filters: React.ReactElement
  loading?: boolean
  allYourPagePath?: string
  resultsCount?: number
}

const FiltersFlyout: React.FC<FiltersFlyoutProps> = (props) => {
  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )
  const dispatch = useDispatch()

  const topRef: React.MutableRefObject<HTMLDivElement | null> = useRef(null)
  const bottomRef: React.MutableRefObject<HTMLDivElement | null> = useRef(null)
  const { analytics } = useAnalyticsContext()

  const screenName = {
    screen: `${calculatedBackgroundLocation.pathname}${props.allYourPagePath}`,
  }

  const closeFiltersFlyout = (): void => {
    dispatch(createSetFiltersFlyoutOpen(false))
    props.onClose && props.onClose()
  }

  const width = 400

  return (
    <RightFlyout
      css={css`
        #flyoutContent {
          margin: 20px;
          border-radius: 10px;
          height: calc(100% - 40px); // TODO: figure out why this is happening
          position: fixed;
          background-color: ${styleConstants.colors.white};
        }
        #childContainer {
          overflow: hidden;
          height: 100%;
        }
      `}
      width={width}
      onClose={(): void => {
        closeFiltersFlyout()
        analytics?.track('filter flyout - click out', screenName)
      }}
      suppressCloseModalAction
      occlusionOpacity={0.45}
    >
      <div
        css={css`
          position: sticky;
          background-color: ${styleConstants.colors.white};
          z-index: 1;
          border-radius: 10px 10px 0px 0px;
          border-bottom: 1px solid ${styleConstants.colors.gray_medium};
        `}
        ref={topRef}
      >
        <div
          css={css`
            padding: 20px 40px;
          `}
        >
          <CloseButton
            css={css`
              ${getBreakpointDown(breakpointSizes.XSM)(css`
                align-self: flex-end;
              `)}
            `}
            design={CloseButtonDesigns.DarkOnLight}
            onClick={(): void => {
              closeFiltersFlyout()
              analytics?.track('filter flyout - close out', screenName)
            }}
            suppressCloseModalAction
          />
          <div
            css={css`
              margin-top: 20px;
              ${getFontCss(
                24,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.medium,
              )};
            `}
          >
            {props.title}
          </div>
        </div>
      </div>
      <div
        css={css`
          padding: 20px 40px 240px;
          overflow: visible scroll;
          height: calc(
            100% -
              ${(bottomRef.current?.clientHeight || 0) +
              (topRef.current?.clientHeight || 0)}px
          );
          position: relative;
        `}
      >
        {props.filters}
      </div>
      <div
        css={css`
          position: sticky;
          display: flex;
          padding: 24px 40px 40px 40px;
          background-color: ${styleConstants.colors.gray_light};
          border-radius: 0px 0px 10px 10px;
          border-top: 1px solid ${styleConstants.colors.gray_medium};
          justify-content: space-between;
          align-items: center;
          width: 100%;
        `}
        ref={bottomRef}
      >
        <HoverableLink
          css={css`
            white-space: normal;
          `}
          onClick={(): void => {
            props.onReset()
            analytics?.track('filter flyout - reset all', screenName)
          }}
          ariaLabelSource="Reset Filters"
        >
          <Trns source={'Reset Filters'} />
        </HoverableLink>
        <Button
          onClick={(): void => {
            closeFiltersFlyout()
            analytics?.track('filter flyout - show results', screenName)
            props.onSave?.()
          }}
          design={ButtonDesigns.designCta}
          text={
            props.resultsCount === undefined ? (
              <Trns source={'Show Results'} />
            ) : (
              <Trns
                source={'Show Results (${count})'}
                expressions={{ count: `${props.resultsCount}` }}
              />
            )
          }
          type={ButtonTypes.submit}
          loading={props.loading}
        />
      </div>
    </RightFlyout>
  )
}

export default FiltersFlyout
